import React, { ReactNode } from "react";
import styled from "styled-components";

type AvatarProps = React.ComponentPropsWithoutRef<"div"> & {
  src?: string;
  alt?: string;
  variant?: string;
  sizes?: string;
  imgProps?: React.ImgHTMLAttributes<HTMLImageElement>;
  children?: ReactNode;
};

// Drop-in for MUI <Avatar>. Circle with initials/children (or image via `src`).
// Forwards ref so it can anchor a <Popover>.
//
// The image is nested rather than rendered *as* the circle: one element cannot be both the
// div this forwards its ref to and an <img>, and the round crop works the same either way.
const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  ({ src, alt, variant, sizes, imgProps, children, ...rest }, ref) => (
    <Circle ref={ref} {...rest}>
      {src ? <img src={src} alt={alt} {...imgProps} /> : children}
    </Circle>
  )
);

export default Avatar;

const Circle = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.fg.accent};
  color: ${({ theme }) => theme.colors.txt.secondary};
  ${({ theme }) => theme.typography.smallText1};
  font-weight: 600;
  text-transform: uppercase;
  cursor: pointer;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;
